'use client';

import CheckBox from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlank from '@mui/icons-material/CheckBoxOutlineBlank';
import { Box, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import type { KeyboardEvent } from 'react';
import { startTransition } from 'react';
import { useShallow } from 'zustand/shallow';
import { usePageAction } from '@repo/hooks/usePageAction';
import { CALCULATOR_INPUT_HEIGHT_PX } from '../../../shared/CalculatorOutlinedInput';
import { formatToDisplayValue } from '../../../utils/calculatorFormMath';
import { useCdCalculatorStore } from '../calculatorStore';
import type { TermLengthUnit } from '../types';

const CHECKBOX_LIKE_ICON_SX = { fontSize: 21, height: 21, width: 21 };

const TERM_LENGTH_RADIO_SX = {
  color: 'neutral.lighter',
  outline: 'none',
  padding: 0,
  '&.Mui-focusVisible': {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    outline: 'none',
  },
  '& .MuiSvgIcon-root': {
    fontSize: 21,
    height: 21,
    width: 21,
  },
  '& input': {
    outline: 'none',
  },
  '& input:focus-visible': {
    outline: 'none',
  },
  '&.Mui-checked': {
    color: 'nwGreens.main',
  },
};

const UNSELECTED_OPTION_HIGHLIGHT_SX = {
  borderColor: 'blues.light',
  '& .MuiRadio-root:not(.Mui-checked)': {
    color: 'blues.light',
  },
};

function termLengthOptionLabelSx(unit: TermLengthUnit, selected: TermLengthUnit) {
  const isSelected = selected === unit;
  return {
    alignItems: 'center',
    backgroundColor: isSelected ? 'greens.extraLight' : 'common.white',
    border: '1px solid',
    borderColor: isSelected ? 'nwGreens.light' : 'neutral.lighter',
    borderRadius: '8px',
    boxSizing: 'border-box',
    cursor: 'pointer',
    display: 'flex',
    flex: '1 1 0%',
    gap: '8px',
    height: CALCULATOR_INPUT_HEIGHT_PX,
    justifyContent: 'center',
    m: 0,
    minHeight: CALCULATOR_INPUT_HEIGHT_PX,
    minWidth: 0,
    paddingX: '8px',
    paddingY: 0,
    width: '100%',
    ...(isSelected
      ? {
          '&:has(input:focus-visible)': {
            outline: '2px solid',
            outlineColor: 'blues.light',
            outlineOffset: 2,
          },
        }
      : {
          '&:hover': UNSELECTED_OPTION_HIGHLIGHT_SX,
          '&:has(input:focus-visible)': UNSELECTED_OPTION_HIGHLIGHT_SX,
        }),
  };
}

type TermLengthUnitToggleProps = {
  ariaLabelledBy?: string;
};

export function TermLengthUnitToggle(props: TermLengthUnitToggleProps = {}) {
  const { ariaLabelledBy } = props;
  const fireCdCalculatorInteractedPageAction = usePageAction({
    actionName: 'cd_calculator_interacted',
    actionSection: 'banking',
    actionType: 'select',
  });

  const { annualPercentageYield, initialDeposit, setField, termLengthUnit, termLength } =
    useCdCalculatorStore(
      useShallow(state => ({
        annualPercentageYield: state.formFields.annualPercentageYield,
        initialDeposit: state.formFields.initialDeposit.includes(',')
          ? state.formFields.initialDeposit.replace(/,/g, '')
          : formatToDisplayValue(state.formFields.initialDeposit),
        setField: state.setField,
        termLengthUnit: state.formFields.termLengthUnit,
        termLength: state.formFields.termLength,
      })),
    );

  function fireCdCalculatorInteracted(overrides?: { termLengthUnitValue?: TermLengthUnit }) {
    fireCdCalculatorInteractedPageAction({
      additional_attributes: {
        annual_percentage_yield_value_input: annualPercentageYield,
        initial_deposit_value_input: initialDeposit,
        term_length_value_input: termLength,
        term_length_unit_value_input: overrides?.termLengthUnitValue ?? termLengthUnit,
      },
    });
  }

  function selectUnit(unit: TermLengthUnit) {
    startTransition(() => {
      fireCdCalculatorInteracted({ termLengthUnitValue: unit });
      setField('termLengthUnit', unit);
    });
  }

  function onEnterSelectUnit(unit: TermLengthUnit) {
    return (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      if (termLengthUnit !== unit) {
        selectUnit(unit);
      }
    };
  }

  return (
    <Box sx={{ minWidth: 0, width: '100%' }}>
      <RadioGroup
        aria-label={ariaLabelledBy ? undefined : 'Term length unit'}
        aria-labelledby={ariaLabelledBy}
        data-testid="term-length-unit-toggle"
        name="cd-calculator-term-length-unit"
        row
        value={termLengthUnit}
        sx={{
          alignItems: 'stretch',
          display: 'flex',
          flexDirection: 'row',
          gap: {
            xs: 2,
            sm: 1,
            md: 3,
          },
          width: '100%',
        }}
        onChange={(_, value) => {
          if (value) selectUnit(value as TermLengthUnit);
        }}
      >
        <FormControlLabel
          value="months"
          control={
            <Radio
              disableRipple
              checkedIcon={<CheckBox sx={CHECKBOX_LIKE_ICON_SX} />}
              icon={<CheckBoxOutlineBlank sx={CHECKBOX_LIKE_ICON_SX} />}
              slotProps={{
                input: {
                  'aria-label': 'Term length in months',
                  onKeyDown: onEnterSelectUnit('months'),
                },
              }}
              sx={TERM_LENGTH_RADIO_SX}
            />
          }
          label={<Typography variant="text">Months</Typography>}
          sx={termLengthOptionLabelSx('months', termLengthUnit)}
        />
        <FormControlLabel
          value="years"
          control={
            <Radio
              disableRipple
              checkedIcon={<CheckBox sx={CHECKBOX_LIKE_ICON_SX} />}
              icon={<CheckBoxOutlineBlank sx={CHECKBOX_LIKE_ICON_SX} />}
              slotProps={{
                input: {
                  'aria-label': 'Term length in years',
                  onKeyDown: onEnterSelectUnit('years'),
                },
              }}
              sx={TERM_LENGTH_RADIO_SX}
            />
          }
          label={<Typography variant="text">Years</Typography>}
          sx={termLengthOptionLabelSx('years', termLengthUnit)}
        />
      </RadioGroup>
    </Box>
  );
}
